import { useMutation } from '@tanstack/react-query';
import { toString } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';
import { findDeleteAssemblyComponents } from './helpers';
import { fetchAssemblyComponents } from './useGetAssemblyComponents';

enum HttpMethod {
  POST = 'POST',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

interface TransactionPayload {
  resource: string;
  method: HttpMethod;
  id?: number;
  data: Record<string, any>;
}

const createAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.CREATE] as (
  assemblyName: string,
  componentName: string,
) => string;

const updateAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.UPDATE] as (
  assemblyName: string,
  componentName: string,
) => string;

const deleteAssemblyComponentNarrativeFn = narrativeGenerators[
  COLLECTIONS.ASSEMBLIES_COMPONENTS
][USER_ACTIONS.DELETE] as (
  assemblyName: string,
  componentName: string,
) => string;

export const useAssemblyTransaction = () => {
  return useMutation({
    mutationFn: assemblyTransaction,
    onSuccess: async () => {
      // Using removeQueries instead of invalidateQueries to completely clear the cache.
      // This ensures fresh data is fetched when navigating to pages that weren't mounted,
      // since invalidateQueries only marks as stale but keeps cached data (problematic with staleTime: Infinity).
      await queryClient.removeQueries(queryKeys.assemblies.invalidateAll());
      await queryClient.invalidateQueries(
        queryKeys.assemblyListing.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.assemblyTags.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.viewAssemblyTagsWithAssembliesCount.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.bulkReplaceEligibleAssemblyIds.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.scheduleAssembliesSearch.invalidateAll(),
      );
      await queryClient.invalidateQueries(queryKeys.components.invalidateAll());
      await queryClient.invalidateQueries(
        queryKeys.componentListing.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.assemblyComponents.invalidateAll(),
      );
      await queryClient.invalidateQueries(
        queryKeys.viewAssembliesWithComponents.invalidateAll(),
      );
    },
  });
};

const assemblyTransaction = async (payload: any) => {
  const TransactionPayload: TransactionPayload[] = [];

  if (payload.updateAssembly) {
    const updateData = {
      ...(payload.updateAssembly.name && {
        name: payload.updateAssembly.name,
        narrative: (
          narrativeGenerators[COLLECTIONS.ASSEMBLIES][USER_ACTIONS.UPDATE] as (
            assemblyName: string,
          ) => string
        )(payload.updateAssembly.name),
      }),
      ...(payload.updateAssembly.status && {
        status: payload.updateAssembly.status,
      }),
      ...(payload.updateAssembly.config && {
        config: payload.updateAssembly.config,
      }),
      ...(payload.updateAssembly.assembly_tag_id && {
        assembly_tag_id: payload.updateAssembly.assembly_tag_id,
      }),
    };

    TransactionPayload.push({
      resource: COLLECTIONS.ASSEMBLIES,
      id: Number(payload.updateAssembly.id),
      method: HttpMethod.PATCH,
      data: updateData,
    });
  }

  if (payload.updateCreateDeleteAssemblyComponents) {
    const updateCreateDeleteAssemblyComponents = {
      ...payload.updateCreateDeleteAssemblyComponents,
      data: payload.updateCreateDeleteAssemblyComponents?.data || [],
    };

    const {
      data: originalAssemblyComponents = [],
      backupAssemblyComponents = [],
    } =
      (await fetchAssemblyComponents(
        toString(updateCreateDeleteAssemblyComponents.assemblyId),
        undefined,
        [],
      )) || {};

    const deleteActions =
      findDeleteAssemblyComponents(
        originalAssemblyComponents,
        backupAssemblyComponents,
        updateCreateDeleteAssemblyComponents.data,
      )?.map((item: any) => {
        const narrative = deleteAssemblyComponentNarrativeFn(
          updateCreateDeleteAssemblyComponents?.assemblyName ?? '',
          item?.component?.name ?? '',
        );

        return {
          resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
          method: HttpMethod.DELETE,
          id: item.id,
          data: { narrative },
        };
      }) || [];

    const updateActions =
      updateCreateDeleteAssemblyComponents.data
        ?.filter((assemblyComponent: any) => assemblyComponent.id)
        ?.map((item: any) => {
          const dataWithoutId = { ...item };
          delete dataWithoutId.id;
          delete dataWithoutId.created_at;
          delete dataWithoutId.modified_at;
          delete dataWithoutId.uid;
          delete dataWithoutId.component;

          const narrative = updateAssemblyComponentNarrativeFn(
            updateCreateDeleteAssemblyComponents?.assemblyName ?? '',
            item?.component?.name ?? '',
          );

          return {
            resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
            method: HttpMethod.PATCH,
            id: item.id,
            data: { ...dataWithoutId, narrative },
          };
        }) || [];

    const createActions =
      updateCreateDeleteAssemblyComponents.data
        ?.filter((item: any) => !item.id)
        ?.map((item: any) => {
          const dataWithoutUid = { ...item };
          delete dataWithoutUid.uid;
          delete dataWithoutUid.component;
          const narrative = createAssemblyComponentNarrativeFn(
            updateCreateDeleteAssemblyComponents?.assemblyName ?? '',
            item?.component?.name ?? '',
          );

          return {
            resource: COLLECTIONS.ASSEMBLIES_COMPONENTS,
            method: HttpMethod.POST,
            data: { ...dataWithoutUid, narrative },
          };
        }) || [];

    TransactionPayload.push(
      ...deleteActions,
      ...updateActions,
      ...createActions,
    );
  }

  const response = await requestCCApi({
    url: '/transaction',
    method: 'POST',
    data: { data: TransactionPayload },
  });

  return response;
};
